import { Box, Button, Grid, Tab, Tabs, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Divider from "@mui/material/Divider";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  fileNameFormat,
  getMatchingSearchResults,
  getPrimaryRegulator,
  isDefinedAndTrue,
} from "../../common/services/Utilities";
import { AppUrl } from "../../config";
import BankFindLocations from "../../containers/BankFindLocations";
import BankFindSort from "../../containers/BankFindSort";
import ResultPagination from "../../containers/ResultPagination";
import {
  Column,
  KeyValue,
  Query,
  ResultData,
  ResultHighlight,
  ResultRecord,
} from "../../interfaces";
import ActiveStatus from "../ActiveStatus";
import ApiDataDownload from "../ApiDataDownload";
import LeavingFDICDialog from "../LeavingFDICDialog";
import LoadingIndicator from "../LoadingIndicator";
import ModalDialog from "../ModalDialog";
import SearchResultCard, {
  CardListCondensedItem,
} from "../SearchResultCard/SearchResultCard";
import ViewInstitutionModalSubtitle from "../ViewInstitutionModalSubtitle";
import BankFindDisruptor from "./BankFindDisruptor";
import ViewDetailsButton from "./ViewDetailsButton";
import MoreInfoButton from "../MoreInfoButton";
import { useBreakpoint } from "../../hooks/useBreakpoint";

const PREFIX = "RecordCount";

const classes = {
  results: `${PREFIX}-results`,
  activeIcon: `${PREFIX}-activeIcon`,
  modalSubtitle: `${PREFIX}-modalSubtitle`,
};

const Root = styled("div")({
  [`& .${classes.results}`]: {
    margin: "0 .5rem",
  },
  [`& .${classes.activeIcon}`]: {
    stroke: "#0ca900",
  },
  [`& .${classes.modalSubtitle}`]: {
    paddingBottom: 10,
  },
});

export interface Props {
  appState: Query;
  resultData: ResultData | null;
  resultHighlight: ResultHighlight;
  recordCount: { active: number; inactive: number };
  dataLoaded: boolean;
  searchPills: React.ReactElement;
  locationValues?: string;
  updatePagination: () => void;
  dataDownloadPost?: KeyValue;
  activeTab: "Active" | "Inactive";
  setActiveTab: React.Dispatch<React.SetStateAction<"Active" | "Inactive">>;
}

export const RecordCount = (recordCount: { [key: string]: number }) => {
  return (
    <Typography variant="h2" id="recordCount">
      {recordCount.recordCount.toLocaleString(navigator.language, {
        minimumFractionDigits: 0,
      })}{" "}
      {recordCount.recordCount === 1 ? "Matching Result" : "Matching Results"}
    </Typography>
  );
};

export default function BankFindResults({
  appState,
  resultData,
  resultHighlight,
  recordCount,
  dataLoaded,
  searchPills,
  locationValues,
  dataDownloadPost,
  activeTab,
  setActiveTab,
  updatePagination,
}: Props) {
  const { xs } = useBreakpoint();

  const [open, setOpen] = React.useState(false);
  const [currentState, setCurrentState] = React.useState(appState);
  const [prevState, setPrevState] = React.useState(appState);
  const [stateChanged, setStateChanged] = React.useState(false);

  const [selectedResultRecord, setSelectedResultRecord] =
    React.useState<ResultRecord>();
  const navigate = useNavigate();
  const handleViewMatchingOffices = (event: any, record: ResultRecord) => {
    event.preventDefault();
    setOpen(true);
    setSelectedResultRecord(record);
  };

  const total =
    activeTab === "Active" ? recordCount.active : recordCount.inactive;

  const showViewMatchingOffices =
    (appState.filters.zip || appState.filters.stalp || appState.search.city) &&
    appState.ui.branchOffices;

  const showTabs =
    appState.filters?.activeStatus?.length &&
    appState.filters?.activeStatus?.length === 2;

  const bankFindLoading = (
    <LoadingIndicator
      text="Loading"
      spinnerSize={32}
      textSx={{
        fontSize: 20,
        width: "100%",
        textAlign: "center",
      }}
      spinnerSx={{
        marginTop: 24,
        width: "100%",
        textAlign: "center",
      }}
    />
  );

  React.useEffect(() => {
    setCurrentState({
      ...appState,
      ui: {
        branchOffices: appState.ui.branchOffices,
      },
      urls: {},
    });
  }, [appState]);

  React.useEffect(() => {
    if (JSON.stringify(currentState) !== JSON.stringify(prevState)) {
      setPrevState(currentState);
      setStateChanged(true);
    }
  }, [currentState, prevState]);

  React.useEffect(() => {
    if (stateChanged && dataLoaded) {
      if (total === 0) {
        setActiveTab(activeTab === "Active" ? "Inactive" : "Active");
      }
      setStateChanged(false);
    }
  }, [activeTab, dataLoaded, setActiveTab, stateChanged, total]);

  React.useEffect(() => {
    if (appState.ui?.activeTab && appState.ui.activeTab !== activeTab) {
      setActiveTab(appState.ui.activeTab);
    }
  }, [activeTab, appState.ui.activeTab, setActiveTab]);

  if (resultData === null) {
    return bankFindLoading;
  } else {
    const BankFindResultsList = resultData.map(
      (resultRecord: ResultRecord, index: number) => {
        const leftColumn = (
          <ActiveStatus institution={resultRecord} appState={appState} />
        );

        const centerColumn = (
          <>
            <Typography component="div" sx={{ paddingBottom: "1rem" }}>
              <CardListCondensedItem
                itemLabel="FDIC Cert #"
                itemValue={resultRecord.CERT}
              />
            </Typography>
            <Typography component="div" sx={{ paddingBottom: "1rem" }}>
              <CardListCondensedItem
                itemLabel="Primary Regulator"
                itemValue={getPrimaryRegulator(resultRecord.REGAGNT)}
              />
            </Typography>
          </>
        );

        const rightColumn = (
          <>
            <Typography component="div" sx={{ paddingBottom: "1rem" }}>
              <CardListCondensedItem
                itemLabel="Headquarters Address"
                itemValue={resultRecord.ADDRESS}
              />
              {isDefinedAndTrue(resultRecord.ADDRESS2) ? (
                <CardListCondensedItem
                  itemValueId={`address_line_2_${resultRecord.CERT}`}
                  itemValue={resultRecord.ADDRESS2}
                />
              ) : (
                ""
              )}
              <CardListCondensedItem
                itemValue={`${resultRecord.CITY}, ${resultRecord.STALP} ${resultRecord.ZIP}`}
              />
            </Typography>
            {resultRecord.WEBADDR && resultRecord.ACTIVE ? (
              <Typography sx={{ paddingBottom: "1rem" }} component="div">
                <CardListCondensedItem
                  itemLabel="Primary Website"
                  itemValue=""
                />
                <LeavingFDICDialog
                  bankName={resultRecord.NAME}
                  webAddress={resultRecord.WEBADDR}
                />
              </Typography>
            ) : (
              ""
            )}
          </>
        );

        const rightSection = (
          <Grid container>
            <Grid
              size={{
                md: 5,
                xs: 12,
                sm: 6,
              }}
            >
              {centerColumn}
            </Grid>
            <Grid
              size={{
                md: 7,
                xs: 12,
                sm: 6,
              }}
            >
              {rightColumn}
            </Grid>
          </Grid>
        );

        const columns: Column[] = [
          {
            widthMd: 4,
            widthXs: 6,
            widthSm: 5,
            content: leftColumn,
          },
          { widthMd: 8, widthXs: 6, widthSm: 7, content: rightSection },
        ];

        const searchMatches = getMatchingSearchResults(resultHighlight[index]);
        return (
          <Grid
            key={`BankFind-${resultRecord.CERT}`}
            size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 6,
            }}
          >
            <div key={`BankFind-${resultRecord.CERT}`}>
              <SearchResultCard
                id={`search-result-card-${index}`}
                title={resultRecord.NAME}
              >
                <Grid
                  container
                  spacing={2}
                  sx={{ height: 150, marginTop: "8px" }}
                >
                  {columns.map((column: Column, colummIndex: number) => {
                    return (
                      <Grid
                        key={`column-${resultRecord.CERT}-${colummIndex}`}
                        sx={{
                          "& .MuiGrid-item": {
                            margin: "0 auto",
                          },
                          "paddingY": 2,
                        }}
                        size={{
                          md: Number(column.widthMd),
                          xs: Number(column.widthXs),
                          sm: Number(column.widthSm),
                        }}
                      >
                        {column.content}
                      </Grid>
                    );
                  })}
                </Grid>
                <Box
                  sx={{
                    display: "flex",
                  }}
                >
                  <Box
                    sx={{
                      "minWidth": 200,
                      "display": "flex",
                      "justifyContent": "center",
                      "gap": 2,
                      "margin": "0 auto",
                      "mt": 2,
                      "mb": -2,
                      "& .MuiButton-root": {
                        margin: "16px 8px 0px 8px",
                        width: 200,
                        height: 40,
                      },
                    }}
                  >
                    <ViewDetailsButton
                      name={resultRecord.NAME}
                      cert={resultRecord.CERT}
                      url={AppUrl("BankFind") + window.location.search}
                    />
                    {resultRecord.ACTIVE ? (
                      <Button
                        aria-label={`View ${
                          showViewMatchingOffices ? "Matching" : "All"
                        } Offices for ${resultRecord.NAME}.`}
                        id={`view-offices-button-${resultRecord.CERT}`}
                        className="viewOfficesButton"
                        onClick={(event: any) =>
                          handleViewMatchingOffices(event, resultRecord)
                        }
                        variant="contained"
                        disableElevation
                        color="secondary"
                      >
                        {`${
                          showViewMatchingOffices
                            ? "Matching Branch"
                            : "View All"
                        } Locations`}
                      </Button>
                    ) : undefined}
                  </Box>
                </Box>
              </SearchResultCard>
              {searchMatches.count ? (
                <BankFindDisruptor
                  cert={resultRecord.CERT}
                  searchValue={
                    appState.search.name
                      ? appState.search.name
                      : appState.search.url
                  }
                  searchMatches={searchMatches}
                />
              ) : undefined}
            </div>
          </Grid>
        );
      },
    );

    const modalSubtitle = (
      <div className={classes.modalSubtitle}>
        Branch Office Information
        <ViewInstitutionModalSubtitle
          onClick={() => {
            navigate(
              {
                pathname: `${AppUrl("BankFind")}/details/${
                  selectedResultRecord ? selectedResultRecord.CERT : ""
                }`,
              },
              {
                state: {
                  url: `${AppUrl("BankFind")}${window.location.search}`,
                },
              },
            );
          }}
        />
      </div>
    );

    const tabLabelFontSize = xs ? { fontSize: 13 } : { fontSize: 16 };
    return (
      <Root>
        <Box
          id="searchResults"
          sx={{
            color: "primary.main",
            margin: xs ? "" : "0 .5rem",
            marginTop: xs ? 0 : -1,
          }}
        >
          <Box
            sx={
              xs
                ? {}
                : {
                    display: "flex",
                    alignItems: "end",
                    justifyContent: "space-between",
                  }
            }
          >
            <RecordCount recordCount={total} />

            <ApiDataDownload
              post={dataDownloadPost}
              app="bankFind"
              apiPathLink="institutions"
              numberOfResults={
                activeTab === "Active"
                  ? recordCount.active
                  : recordCount.inactive
              }
              apiData={[
                {
                  appState: {
                    ...appState,
                    filters: {
                      ...appState.filters,
                      activeStatus: activeTab === "Active" ? [1] : [0],
                    },
                  },
                  apiPath: "institutions",
                  fileName: `${fileNameFormat("Institutions")}.csv`,
                },
              ]}
            />
          </Box>
          <Divider />
          {searchPills}
          {showTabs && (
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  "borderBottom": 1,
                  "borderColor": "divider",
                  "& .MuiTabs-fixed": { overflow: "visible !important" },
                  "& .MuiTabs-root": { overflow: "visible !important" },
                }}
              >
                <Tabs
                  value={false}
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                  aria-label="tabs"
                  variant="fullWidth"
                >
                  {(["Active", "Inactive"] as const).map((tab, index) => (
                    <Tab
                      iconPosition={"end"}
                      icon={
                        <MoreInfoButton
                          id="active-inactive-info"
                          popperPosition={"auto"}
                          label={
                            index === 0
                              ? "Active Institution Information"
                              : "Inactive Institution Information"
                          }
                          hover
                          wide={350}
                        >
                          <Typography
                            variant="h3"
                            sx={{
                              textAlign: "center",
                              marginBottom: 0,
                            }}
                          >
                            {index === 0
                              ? "Active Institutions"
                              : "Inactive Institutions"}
                          </Typography>
                          <Typography variant="body2" style={{ padding: 0 }}>
                            {index === 0
                              ? "Institutions that are currently open and insured by the FDIC."
                              : "Institutions that have either closed or are no longer doing business under their previously issued FDIC CERT (due to merger into another institution, closure, or failure); or an institution that no longer carries FDIC insurance."}
                          </Typography>
                        </MoreInfoButton>
                      }
                      label={`${tab} Institutions - ${
                        index === 0
                          ? recordCount.active.toLocaleString()
                          : recordCount.inactive.toLocaleString()
                      } Results`}
                      id={`tab-${tab.toLowerCase()}`}
                      key={tab}
                      tabIndex={0}
                      aria-label={`${tab} Institutions tab`}
                      onClick={() => {
                        setActiveTab(tab);
                        updatePagination();
                      }}
                      sx={{
                        "overflow": "visible",
                        "marginRight": "10px",
                        "marginLeft": "10px",
                        "backgroundColor":
                          activeTab === tab ? "#1A4480" : "#f0f0f0",
                        "color": activeTab === tab ? "#ffffff" : "#1A4480",
                        "&:hover": {
                          backgroundColor: activeTab === tab ? "" : "#D9E8F6",
                          color: activeTab === tab ? "#ffffff" : "#1A4480",
                        },
                        "&.Mui-focusVisible": {
                          backgroundColor: "#D9E8F6",
                          color: "#1A4480",
                        },
                        "textTransform": "none",
                        "fontFamily": "Source Sans Pro",
                        ...tabLabelFontSize,
                        "fontWeight": "bold",
                        "width": "100%",
                        "height": "35px",
                      }}
                    />
                  ))}
                </Tabs>
              </Box>
            </Box>
          )}
          {BankFindResultsList?.length > 0 ? (
            <ResultPagination count={total}>
              <>
                <BankFindSort />
                {!dataLoaded ? (
                  bankFindLoading
                ) : (
                  <Grid container spacing={2} key={`BankFind-results`}>
                    {BankFindResultsList}
                  </Grid>
                )}
              </>
            </ResultPagination>
          ) : (
            <Typography
              variant="h1"
              sx={{
                paddingTop: "30px",
                marginLeft: "10px",
                color: "#1A4480",
                textTransform: "none",
                fontFamily: "Source Sans Pro",
                fontSize: "24px",
                fontWeight: "bold",
                width: "100%",
                textAlign: "center",
              }}
            >
              No Results
            </Typography>
          )}
        </Box>
        <ModalDialog
          title={
            selectedResultRecord
              ? `${selectedResultRecord.NAME} - #${selectedResultRecord.CERT}`
              : ""
          }
          display={open}
          setDisplay={setOpen}
          subtitle={modalSubtitle}
          maxWidth="lg"
        >
          <BankFindLocations
            cert={selectedResultRecord ? selectedResultRecord.CERT : ""}
            locationsSearch={locationValues}
          />
        </ModalDialog>
      </Root>
    );
  }
}
